// src/pages/RefundPolicyPage.jsx
import React from "react";
import { Helmet } from "react-helmet";

const SUPPORT_EMAIL = "artparcel.sup@gmail.com";   // TODO: replace
const COMPANY_NAME  = "ArtParcel";                   // TODO: replace if needed
const SITE_URL      = "https://artparcel.club";     // TODO: replace
const LAST_UPDATED  = "September 15, 2025";          // keep explicit date

const RefundPolicyPage = () => {
  return (
    <div className="bg-slate-900 text-white min-h-screen flex flex-col">
      <Helmet>
        <title>Refund Policy | {COMPANY_NAME}</title>
        <meta name="description" content={`${COMPANY_NAME} Refunds and Returns Policy`} />
        <meta property="og:title" content={`Refund Policy | ${COMPANY_NAME}`} />
        <meta property="og:description" content={`${COMPANY_NAME} Refunds and Returns Policy`} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={`${SITE_URL}/refunds`} />
      </Helmet>

      <main className="flex-1">
        <div className="max-w-4xl mx-auto px-4 py-16">
          <header className="mb-10 text-center">
            <h1 className="text-4xl font-bold">Refund Policy</h1>
            <p className="text-sm text-slate-400 mt-2">Last updated: {LAST_UPDATED}</p>
            <p className="text-xs text-slate-500 mt-1 italic">
              This page provides general information about refunds for digital items on {COMPANY_NAME}. It is not legal advice.
            </p>
          </header>

          {/* Table of Contents */}
          <nav className="mb-12 rounded-2xl border border-slate-800 bg-slate-950/60 p-4">
            <h2 className="text-lg font-semibold mb-2">Table of Contents</h2>
            <ul className="text-sm text-slate-300 grid sm:grid-cols-2 gap-y-1 gap-x-6 list-disc list-inside">
              <li><a href="#summary" className="hover:text-white">1. Summary</a></li>
              <li><a href="#eligibility" className="hover:text-white">2. Eligibility for Refunds</a></li>
              <li><a href="#nonrefundable" className="hover:text-white">3. Non-Refundable Items</a></li>
              <li><a href="#howto" className="hover:text-white">4. How to Request a Refund</a></li>
              <li><a href="#processing" className="hover:text-white">5. Processing & Timelines</a></li>
              <li><a href="#chargebacks" className="hover:text-white">6. Disputes & Chargebacks</a></li>
              <li><a href="#effects" className="hover:text-white">7. Effects of Refunds (Pixel Drops/Artwork)</a></li>
              <li><a href="#subscriptions" className="hover:text-white">8. Subscriptions (if applicable)</a></li>
              <li><a href="#regional" className="hover:text-white">9. Regional Rights</a></li>
              <li><a href="#changes" className="hover:text-white">10. Changes to This Policy</a></li>
              <li><a href="#contact" className="hover:text-white">11. Contact Us</a></li>
            </ul>
          </nav>

          {/* Sections */}
          <section id="summary" className="mb-10">
            <h2 className="text-2xl font-semibold mb-3">1. Summary</h2>
            <p className="text-slate-300">
              {COMPANY_NAME} offers digital goods and in-app features (e.g., Pixel Drops to claim/renew artwork). In general,
              purchases are <span className="font-semibold">final and non-refundable</span>. We may provide refunds in limited cases
              like duplicate charges, unauthorized transactions, or delivery failures, and as required by law.
            </p>
          </section>

          <section id="eligibility" className="mb-10">
            <h2 className="text-2xl font-semibold mb-3">2. Eligibility for Refunds</h2>
            <ul className="text-slate-300 list-disc list-inside space-y-2">
              <li><span className="font-semibold">Duplicate charge:</span> You were charged more than once for the same order.</li>
              <li><span className="font-semibold">Unauthorized charge:</span> Payment was not authorized by you (we may request verification).</li>
              <li><span className="font-semibold">Failed delivery:</span> Pixel Drops weren’t credited to your balance within a reasonable time.</li>
              <li><span className="font-semibold">Processing error:</span> A clear payment processor or system error occurred.</li>
              {/* Optional policy levers — choose your stance and remove TODOs */}
              <li className="text-slate-400 text-xs">
                Mistaken purchase not used/consumed within <span className="italic">X hours/days</span> may be considered.
              </li>
            </ul>
            <p className="text-slate-400 text-xs mt-2">
              We may decline requests that don’t meet the criteria or appear abusive (e.g., repeated refund requests after consumption).
            </p>
          </section>

          <section id="nonrefundable" className="mb-10">
            <h2 className="text-2xl font-semibold mb-3">3. Non-Refundable Items</h2>
            <ul className="text-slate-300 list-disc list-inside space-y-2">
              <li>Pixel Drops that have been spent, transferred in-app, or used to modify the canvas.</li>
              <li>Promotional/bonus Pixel Drops given for free as part of events or bundles.</li>
              <li>Gifts/donations or voluntary tips (if enabled).</li>
              <li>Any items expressly marked “non-refundable,” except where required by law.</li>
            </ul>
          </section>

          <section id="howto" className="mb-10">
            <h2 className="text-2xl font-semibold mb-3">4. How to Request a Refund</h2>
            <p className="text-slate-300">
              Email <a href={`mailto:${SUPPORT_EMAIL}?subject=Refund%20Request`} className="text-indigo-400 underline">{SUPPORT_EMAIL}</a>{" "}
              from the account email used for the purchase. Include:
            </p>
            <ul className="text-slate-300 list-disc list-inside space-y-2 mt-2">
              <li>Order ID / receipt, purchase date, and amount charged.</li>
              <li>Description of the issue (duplicate, unauthorized, failed delivery, etc.).</li>
              <li>Any relevant screenshots. Do not send full card numbers.</li>
            </ul>
            <p className="text-slate-400 text-xs mt-2">
              TODO: set a time window (e.g., requests must be made within <span className="italic">7 days</span> of purchase).
            </p>
          </section>

          <section id="processing" className="mb-10">
            <h2 className="text-2xl font-semibold mb-3">5. Processing & Timelines</h2>
            <ul className="text-slate-300 list-disc list-inside space-y-2">
              <li>Approved refunds are issued to the original payment method via our processor (e.g., Stripe).</li>
              <li>It may take <span className="font-semibold">5–10 business days</span> for funds to appear depending on your bank.</li>
              <li>We can’t refund to a different card or as cash.</li>
              <li>Taxes and fees are refunded where required by law or by processor rules.</li>
            </ul>
          </section>

          <section id="chargebacks" className="mb-10">
            <h2 className="text-2xl font-semibold mb-3">6. Disputes & Chargebacks</h2>
            <p className="text-slate-300">
              If you believe a charge is incorrect, please contact us first at{" "}
              <a href={`mailto:${SUPPORT_EMAIL}?subject=Billing%20Issue`} className="text-indigo-400 underline">{SUPPORT_EMAIL}</a>.
              Filing a chargeback before contacting us may delay resolution. During investigations, your account or token balance
              may be temporarily limited to prevent abuse.
            </p>
          </section>

          <section id="effects" className="mb-10">
            <h2 className="text-2xl font-semibold mb-3">7. Effects of Refunds (Pixel Drops/Artwork)</h2>
            <ul className="text-slate-300 list-disc list-inside space-y-2">
              <li>When a refund is granted, we will deduct the corresponding Pixel Drops from your balance.</li>
              <li>If Pixel Drops were already spent, we may remove or revert associated artwork changes where feasible.</li>
              <li>Abuse of the refund process can lead to suspension or termination under our <a href="/terms" className="text-indigo-400 underline">Terms</a>.</li>
            </ul>
          </section>

          <section id="subscriptions" className="mb-10">
            <h2 className="text-2xl font-semibold mb-3">8. Subscriptions (if applicable)</h2>
            <ul className="text-slate-300 list-disc list-inside space-y-2">
              <li className="text-slate-400 text-xs">
                TODO: If you add subscriptions, specify renewal cadence, cancellation cut-off, and whether you prorate.
              </li>
              <li>Generally, subscription charges are non-refundable for the current period once started.</li>
              <li>You can cancel future renewals at any time in your account or by emailing support.</li>
            </ul>
          </section>

          <section id="regional" className="mb-10">
            <h2 className="text-2xl font-semibold mb-3">9. Regional Rights</h2>
            <p className="text-slate-300 mb-2">
              Your rights may vary based on your location. Where applicable, we will honor consumer protection laws.
            </p>
            <ul className="text-slate-300 list-disc list-inside space-y-2">
              <li><span className="font-semibold">EEA/UK:</span> Some digital content may forfeit the 14-day withdrawal right once delivery begins with your consent.</li>
              <li><span className="font-semibold">California:</span> We comply with applicable consumer law requirements. See our{" "}
                <a href="/privacy" className="text-indigo-400 underline">Privacy Policy</a> for privacy rights.</li>
            </ul>
          </section>

          <section id="changes" className="mb-10">
            <h2 className="text-2xl font-semibold mb-3">10. Changes to This Policy</h2>
            <p className="text-slate-300">
              We may update this Refund Policy from time to time. Material changes will be posted here with an updated
              “Last updated” date. Continued use of the Service after changes become effective constitutes acceptance.
            </p>
          </section>

          <section id="contact" className="mb-10">
            <h2 className="text-2xl font-semibold mb-3">11. Contact Us</h2>
            <p className="text-slate-300">
              Questions about refunds? Email{" "}
              <a href={`mailto:${SUPPORT_EMAIL}`} className="text-indigo-400 hover:text-indigo-300 underline">
                {SUPPORT_EMAIL}
              </a>.
            </p>
          </section>

          {/* Back to top */}
          <div className="mt-12 flex justify-center">
            <a
              href="#top"
              onClick={(e) => { e.preventDefault(); window.scrollTo({ top: 0, behavior: "smooth" }); }}
              className="text-sm text-slate-300 hover:text-white underline"
            >
              Back to top
            </a>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-slate-800 bg-slate-900/80">
        <div className="max-w-6xl mx-auto px-4 py-6 flex flex-col sm:flex-row items-center justify-between gap-3">
          <div className="text-sm text-slate-400">© {new Date().getFullYear()} {COMPANY_NAME}</div>
          <nav className="flex flex-wrap items-center gap-x-6 gap-y-2 text-sm">
            <a href="/Terms/Terms" className="text-slate-300 hover:text-white">Terms</a>
            <a href="/Terms/Privacy" className="text-slate-300 hover:text-white">Privacy</a>
            <a href="/Terms/Refunds" className="text-slate-300 hover:text-white">Refund Policy</a>
            <a href={`mailto:${SUPPORT_EMAIL}`} className="text-slate-300 hover:text-white">{SUPPORT_EMAIL}</a>
          </nav>
        </div>
      </footer>
    </div>
  );
};

export default RefundPolicyPage;
